/**
 * iter701 — ZATCA Phase 1 TLV parser.
 *
 * Saudi Arabia mandates a QR code on every B2C invoice carrying 5 fields
 * encoded as TLV (Tag-Length-Value) bytes in this exact order:
 *
 *   1. Seller name
 *   2. VAT registration number
 *   3. Timestamp of the invoice (ISO 8601, sometimes Unix)
 *   4. Invoice total (including VAT)
 *   5. VAT total
 *
 * Each field is `[tag:1B][length:1B][utf-8 bytes]`. The whole TLV
 * sequence is then Base64-encoded into the QR payload.
 *
 * We also gracefully handle ZATCA Phase 2 (cryptographic stamp + UUID)
 * by just surfacing the extra tags as raw strings. Anything that isn't
 * a valid ZATCA TLV (e.g. a plain URL on a non-Saudi invoice) is
 * returned as `{ raw: <text> }` so the UI can still let the user
 * proceed manually.
 */

const TAG_NAMES = {
  1: 'seller_name',
  2: 'vat_number',
  3: 'invoice_timestamp',
  4: 'invoice_total',
  5: 'vat_amount',
  6: 'invoice_hash',       // Phase 2
  7: 'crypto_stamp',       // Phase 2
  8: 'public_key',         // Phase 2
  9: 'crypto_signature',   // Phase 2
};

/** Decode a base64 string into a Uint8Array. */
function b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Read a UTF-8 substring from a Uint8Array. */
function bytesToString(bytes, start, len) {
  const slice = bytes.slice(start, start + len);
  return new TextDecoder('utf-8').decode(slice);
}

/**
 * Parse a QR payload into an object the UI can pre-fill the form with.
 *
 * @returns {{ ok: boolean, fields: Record<string,string>, raw: string, format: 'zatca'|'unknown' }}
 */
export function parseInvoiceQR(payload) {
  if (!payload || typeof payload !== 'string') {
    return { ok: false, fields: {}, raw: '', format: 'unknown' };
  }

  // ZATCA QR is always base64 — try that first.
  let bytes;
  try {
    bytes = b64ToBytes(payload.trim());
  } catch {
    // Not base64? Could be a URL or arbitrary text — return as-is.
    return { ok: false, fields: {}, raw: payload, format: 'unknown' };
  }

  const fields = {};
  let i = 0;
  let parsedAtLeastOne = false;
  while (i < bytes.length) {
    const tag = bytes[i];
    const len = bytes[i + 1];
    if (typeof tag !== 'number' || typeof len !== 'number') break;
    if (i + 2 + len > bytes.length) break;  // Truncated payload — bail.
    const value = bytesToString(bytes, i + 2, len);
    const name = TAG_NAMES[tag] || `tag_${tag}`;
    fields[name] = value;
    parsedAtLeastOne = true;
    i += 2 + len;
  }

  if (!parsedAtLeastOne) {
    return { ok: false, fields: {}, raw: payload, format: 'unknown' };
  }

  // Normalise the timestamp to ISO if it looks like a Unix timestamp.
  if (fields.invoice_timestamp && /^\d{10,13}$/.test(fields.invoice_timestamp)) {
    const ms = fields.invoice_timestamp.length === 13
      ? Number(fields.invoice_timestamp)
      : Number(fields.invoice_timestamp) * 1000;
    fields.invoice_timestamp = new Date(ms).toISOString();
  }

  // Coerce numeric fields so the UI can display / compute with them.
  if (fields.invoice_total) fields.invoice_total_num = parseFloat(fields.invoice_total) || 0;
  if (fields.vat_amount)    fields.vat_amount_num    = parseFloat(fields.vat_amount)    || 0;
  if (fields.invoice_total_num && fields.vat_amount_num != null) {
    fields.subtotal_num = +(fields.invoice_total_num - fields.vat_amount_num).toFixed(2);
  }

  return { ok: true, fields, raw: payload, format: 'zatca' };
}

export default parseInvoiceQR;
